import {isNearAirport} from '@/utils/airports'
import {tripPickupPoint} from '@/features/fromAirportArrival/arrivalEligibility'

/**
 * THE TRIP'S FLIGHT, READ OFF THE TRIP — the backend's record, not a lookup of our own.
 *
 * ── ⚠️ THIS REPLACES A BILLED AERODATABOX CALL MADE FROM THE BROWSER ──────────────
 * The arrival screens used to ask the provider directly, once a minute, with the key
 * shipped in the bundle. The backend now polls the flight itself (~15 min while tracking
 * is active, from the moment the booking is confirmed) and puts the whole answer on the
 * trip as `trip.flight`, so there is nothing left for a client to look up. The FE guide
 * is explicit about it: render flight data FROM THE SERVER, never call the provider.
 *
 * What comes back under `provider_data` is the provider's response body untouched — the
 * same shape the direct call returned — so everything downstream that already reads
 * `departure` / `arrival` / `status` keeps reading exactly what it read before.
 *
 * ── ⚠️ `provider_data` IS A LIST, AND PICKING [0] IS A REAL BUG ───────────────────
 * The provider returns one entry per leg or codeshare match. A single-leg flight has one
 * and [0] is right by luck; a multi-leg number under one code (YVR → YYZ → YUL) has
 * several, and the first of them is whichever leg the provider listed first — not the one
 * this passenger is getting off. Matching the trip's own airport is what makes it the
 * right leg rather than usually the right leg.
 *
 * ── ⚠️ NULL MEANS "NOT POLLED YET", NEVER "NO SUCH FLIGHT" ────────────────────────
 * `provider_data` is null until the first poll lands, and a trip booked further out than
 * the provider publishes schedules for stays null through many daily refreshes without
 * tracking having failed. Callers must read null as "no answer yet".
 */

/**
 * The airport this trip is collected FROM, as an IATA code.
 *
 * ⚠️ THE TRIP NAMES ITS AIRPORT BY COORDINATE, NOT BY CODE. There is no `airport_iata`
 * on the resource — the pickup is a point, and which airport it is comes from matching
 * that point against the fetched airport list, exactly as `hasArrivalGuide` does. Reading
 * a code field that does not exist would return undefined on every trip and quietly
 * disable the leg matching below.
 *
 * Null before the airport list lands, or for a pickup that is not at an airport.
 */
function tripAirportIata(trip) {
    const point = tripPickupPoint(trip)
    if (!point) return null
    const code = String(isNearAirport(point.lat, point.lng)?.code || '').trim().toUpperCase()
    return code || null
}

/**
 * The leg of `provider_data` this trip is actually meeting.
 *
 * ⚠️ FALLS BACK TO THE FIRST ENTRY, and that is not the same as defaulting to it. With one
 * entry there is nothing to choose between; with several and no trip airport to match on,
 * the first is the provider's own primary and the only answer available. Refusing to
 * answer would blank a screen that has a usable flight in front of it.
 */
export function tripFlightRecord(trip) {
    const legs = trip?.flight?.provider_data
    if (!Array.isArray(legs) || legs.length === 0) return null
    if (legs.length === 1) return legs[0]

    const iata = tripAirportIata(trip)
    if (iata) {
        const match = legs.find(
            (leg) => String(leg?.arrival?.airport?.iata || '').trim().toUpperCase() === iata,
        )
        if (match) return match
    }
    return legs[0]
}

/**
 * The backend's own summary block — `tracking_status`, `delay_minutes`,
 * `expected_landing_at`, `last_polled_at`.
 *
 * ⚠️ SEPARATE FROM THE PROVIDER RECORD ON PURPOSE. These fields are the backend's stable
 * contract; `provider_data` is the provider's shape and may change under us. Where both
 * could answer a question, this is the one to ask.
 */
export function tripFlightSummary(trip) {
    return trip?.flight || null
}

export default tripFlightRecord
